import { Document, Gallery, Video } from 'iconsax-react';
import type { ReactNode } from 'react';
import VideoViewer from '@/components/common/media-content/video-viewer';
import { SafeImage } from '@/components/common/safe-image';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import cn from '@/utils/merge-class-names';

interface Props {
  className?: string;
  type: ConversationMessageContentType;
  src: string;
}

export default function MediaPreviewContent({ type, src, className }: Props) {
  let content: ReactNode;

  switch (type) {
    case ConversationMessageContentType.Image:
      content = (
        <>
          <SafeImage
            src={src}
            className={cn('size-full bg-cover rounded', className)}
            alt='media preview content'
          />
          <Gallery
            variant='Bold'
            className='size-8 p-1.5 absolute bottom-0 start-0 text-neutral-0'
          />
        </>
      );
      break;
    case ConversationMessageContentType.Video:
      content = (
        <>
          <VideoViewer
            src={src}
            respectRatio={false}
            preload='metadata'
            className={cn('size-full object-cover bg-cover pointer-events-none rounded', className)}
            autoPlay={false}
            controls
          />
          <Video
            variant='Bold'
            className='size-8 p-1.5 absolute bottom-0 start-0 text-neutral-0'
          />
        </>
      );
      break;
    case ConversationMessageContentType.File:
      {
        const [filename = '', fileSize = ''] = src.split('|');
        content = (
          <>
            <Document
              variant='Outline'
              className='size-7 p-1.5 absolute top-2 start-0 text-neutral-700'
            />
            <div className={cn('p-2 pt-8 size-full rounded overflow-hidden', className)}>
              <span className='break-all line-clamp-4 overflow-hidden text-ellipsis text-sm leading-6'>{filename}</span>

              {fileSize && <span className='text-sm'>{fileSize}</span>}
            </div>
          </>
        );
      }
      break;
    default:
      content = (
        <div className={cn('p-2 pt-8 size-full rounded overflow-hidden', className)}>
          <div className='break-all line-clamp-4 overflow-hidden text-ellipsis text-sm leading-6'>{src}</div>
        </div>
      );
      break;
  }

  return (
    <div
      className={cn('relative aspect-square rounded overflow-hidden', {
        'border border-neutral-200': [
          ConversationMessageContentType.Text,
          ConversationMessageContentType.File,
        ].includes(type),
      })}>
      {content}
    </div>
  );
}
